import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Filter, ChevronDown, Zap, X, Compass,
  CalendarIcon, Bookmark, Search,
  Sparkles, Sun, Car, Clock,
} from "lucide-react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "@/lib/date-format";
import { supabase } from "@/integrations/supabase/client";
import type { WizardAnswers, DurationPreference } from "@/lib/wizard-types";
import { matchesDurationPreference, parseDurationHours } from "@/lib/wizard-types";
import { parseExtras } from "@/lib/extras-signals";
import { driveMinutesFor, LWR_CENTER, extractZipFromAddress, type LatLng } from "@/lib/distance";
import { geocodeZip } from "@/lib/geocode.functions";
import { getWeatherForecast, type WeatherForecast } from "@/lib/weather.functions";
import { getWeatherIcon } from "@/lib/weather-display";
import { bumpExploreActivity } from "@/lib/explore-activity";
import { extractSpecificDates, hasSpecificDates, datesInRange, matchesSelectedWindow, isOpenOnWeekday, isOpenOnAnyWeekday } from "@/lib/event-schedule";
import { EventCard } from "@/components/EventCard";
import { buildSeoMeta, SITE_URL, SITE_NAME } from "@/lib/seo";
import { EventUserActions } from "@/components/EventUserActions";
import { useAuth } from "@/lib/auth-context";
import { getMyHistory } from "@/lib/user-data.functions";
import { useServerFn } from "@tanstack/react-start";

export const Route = createFileRoute("/explore")({
  head: () => {
    const seo = buildSeoMeta({
      title: "Your Picks — Lakewood Ranch — RanchPlanner",
      description:
        "Browse curated things to do in Lakewood Ranch, FL — events, parks, restaurants, family fun, and indoor activities tailored to your day.",
      path: "/explore",
    });
    const collectionJsonLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Things To Do In Lakewood Ranch, FL",
      url: `${SITE_URL}/explore`,
      description:
        "Curated events, parks, restaurants, and activities in and around Lakewood Ranch, Florida.",
      isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
      about: { "@type": "Place", name: "Lakewood Ranch, FL" },
    };
    return {
      ...seo,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(collectionJsonLd) },
      ],
    };
  },
  component: ResultsPage,
});

interface DBEvent {
  id: string;
  title: string;
  description: string;
  category: string;
  venue_name: string;
  venue_address: string;
  source_url: string;
  map_url: string;
  source_name: string;
  schedule_notes: string;
  start_date: string | null;
  end_date: string | null;
  cost_level: string;
  cost_details: string;
  indoor_outdoor: string;
  age_range: string;
  crowd_level: string;
  intensity: number;
  novelty_score: number;
  weather_sensitivity: number;
  estimated_duration: string;
  drive_minutes_from_lwr: number;
  lat: number | null;
  lng: number | null;
  parent_id: string | null;
  image_url: string;
  save_count: number;
  link_click_count: number;
}

function parseCostNumeric(text: string): number {
  if (/free/i.test(text)) return 0;
  // Strip parking-only price callouts ("$5 parking", "+$5 parking",
  // "parking $10") so a free-entry venue with paid parking doesn't get
  // priced at the parking fee.
  const cleaned = text
    .replace(/[+]?\s*\$\d+\s*(?:\/\s*\w+\s*)?parking[^,;.]*/gi, "")
    .replace(/parking[^,;.]*?\$\d+[^,;.]*/gi, "");
  const matches = [...cleaned.matchAll(/\$(\d+)/g)];
  if (matches.length === 0) return /free/i.test(cleaned) ? 0 : 999;
  return Math.min(...matches.map((m) => parseInt(m[1], 10)));
}

function formatCost(details: string, level: string): string {
  if (!details) return level;
  let cleaned = details
    .replace(/[,;]?\s*food\s+(purchases?\s+)?extra/gi, "")
    .replace(/[,;]?\s*food\s+not\s+included/gi, "")
    .replace(/[,;]?\s*additional\s+food\s+costs?/gi, "")
    .replace(/[,;.]?\s*donations?\s+appreciated\.?/gi, "")
    .replace(/[,;.]?\s*suggested\s+donation[^.,;]*/gi, "")
    .replace(/[,;.]?\s*vendor\s+prices?\s+vary\.?/gi, "")
    .replace(/[,;.]?\s*prices?\s+vary[^.,;]*/gi, "")
    .replace(/\bapproximately\s+/gi, "")
    .replace(/\broughly\s+/gi, "")
    .replace(/\bstarting\s+(at|around)\s+/gi, "")
    .replace(/\baround\s+\$/gi, "$")
    .replace(/^\s*[,;.]\s*/, "")
    .replace(/\s*[,;.]\s*$/, "")
    .trim();
  if (!cleaned) return /free/i.test(level) || !level ? "Free" : level;
  return cleaned;
}


// All date math is anchored to America/New_York (EST/EDT) so "today",
// "tomorrow", and "weekend" reflect the user's local Florida day, not UTC.
const APP_TZ = "America/New_York";

// Returns the current date in EST as { y, m, d } (m is 1-12).
function nowInEST(): { y: number; m: number; d: number; dayOfWeek: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value || "";
  const y = parseInt(get("year"), 10);
  const m = parseInt(get("month"), 10);
  const d = parseInt(get("day"), 10);
  const wkMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dayOfWeek = wkMap[get("weekday")] ?? 0;
  return { y, m, d, dayOfWeek };
}

function toISODate(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// Add N days to a Y/M/D triple using UTC math (safe arithmetic, no TZ drift).
function addDaysISO(y: number, m: number, d: number, days: number): string {
  const t = Date.UTC(y, m - 1, d) + days * 86400000;
  const dt = new Date(t);
  return toISODate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

function todayISO_EST(): string {
  const { y, m, d } = nowInEST();
  return toISODate(y, m, d);
}

function tomorrowISO_EST(): string {
  const { y, m, d } = nowInEST();
  return addDaysISO(y, m, d, 1);
}

function getWeekendDates(): { start: string; end: string } {
  const { y, m, d, dayOfWeek } = nowInEST();
  if (dayOfWeek === 0) {
    // Sunday — weekend is just today
    const today = toISODate(y, m, d);
    return { start: today, end: today };
  }
  if (dayOfWeek === 6) {
    // Saturday — Sat + Sun
    return { start: toISODate(y, m, d), end: addDaysISO(y, m, d, 1) };
  }
  // Mon–Fri — upcoming Sat + Sun
  const daysUntilSat = 6 - dayOfWeek;
  return { start: addDaysISO(y, m, d, daysUntilSat), end: addDaysISO(y, m, d, daysUntilSat + 1) };
}

// Day-of-week name for an ISO date string (parsed as EST noon to avoid TZ drift)
function dayNameForISO(iso: string): string {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  // Use UTC noon — guarantees same calendar day regardless of viewer TZ.
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return days[dt.getUTCDay()];
}

// Weekday index (0=Sun … 6=Sat) for an ISO date string.
function weekdayIndexForISO(iso: string): number {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return dt.getUTCDay();
}

function formatDateLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}

function ResultsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const getHistoryFn = useServerFn(getMyHistory);
  const [wizardData, setWizardData] = useState<WizardAnswers | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [events, setEvents] = useState<DBEvent[]>([]);
  // ZIP → lat/lng map used to anchor events that don't have venue coordinates.
  // Keyed by 5-digit ZIP parsed from venue_address. Prefetched from zip_cache
  // and lazily filled via geocodeZip for any ZIPs missing from cache.
  const [zipCoords, setZipCoords] = useState<Map<string, LatLng>>(new Map());
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState<WeatherForecast | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [randomSeed, setRandomSeed] = useState(0);

  // Ranking mode: "smart" or "shuffle"
  const [rankMode, setRankMode] = useState<"smart" | "shuffle">("shuffle");

  // Filters — default timeframe set after wizard check
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [settingFilter, setSettingFilter] = useState<string>("all");
  const [distanceFilter, setDistanceFilter] = useState<string>("all");
  const [costFilter, setCostFilter] = useState<string>("all");
  const [timeframeFilter, setTimeframeFilter] = useState<string>("weekend");
  const [durationFilter, setDurationFilter] = useState<DurationPreference>("any");
  const [customDateRange, setCustomDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [draftDateRange, setDraftDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showBeyondCap, setShowBeyondCap] = useState(false);
  const [smartHintDismissed, setSmartHintDismissed] = useState<boolean>(false);

  // Personalization state — signed-in users only. Maps event_id → most recent
  // visit date (ISO) and event_id → 1-5 rating. Drives novelty boosts,
  // recency penalty, and category affinity in the ranker, plus the optional
  // "Been here?" + star controls on each card.
  const [visitsMap, setVisitsMap] = useState<Map<string, string>>(new Map());
  const [ratingsMap, setRatingsMap] = useState<Map<string, number>>(new Map());
  // Rating/visit changes shouldn't reshuffle the visible list. Personalization
  // reads the latest values through refs so the `filtered` memo doesn't have
  // to depend on these maps — the list only re-sorts when filter/sort controls
  // at the top of the page change.
  const visitsMapRef = useRef(visitsMap);
  const ratingsMapRef = useRef(ratingsMap);
  useEffect(() => { visitsMapRef.current = visitsMap; }, [visitsMap]);
  useEffect(() => { ratingsMapRef.current = ratingsMap; }, [ratingsMap]);

  useEffect(() => {
    if (!user) {
      setVisitsMap(new Map());
      setRatingsMap(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getHistoryFn();
        if (cancelled || !res.ok) return;
        const v = new Map<string, string>();
        for (const row of res.visits) {
          // visits ordered newest first — first hit per event wins.
          if (!v.has(row.event_id)) v.set(row.event_id, row.visited_on);
        }
        const r = new Map<string, number>();
        for (const row of res.ratings) r.set(row.event_id, row.rating);
        setVisitsMap(v);
        setRatingsMap(r);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [user, getHistoryFn]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setRandomSeed(Math.random());
      try {
        if (sessionStorage.getItem("rp-smart-hint-dismissed") === "1") {
          setSmartHintDismissed(true);
        }
      } catch { /* noop */ }
      const stored = sessionStorage.getItem("rp-wizard");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setWizardData(parsed);
          // Default to smart ranked when wizard data exists
          setRankMode("smart");

          if (parsed.duration && parsed.duration !== "any") {
            setDurationFilter(parsed.duration as DurationPreference);
          }

          if (parsed.budgetExclude?.length) {
            const excl = parsed.budgetExclude as string[];
            if (excl.includes("low") && excl.includes("medium") && excl.includes("high")) {
              setCostFilter("free");
            } else if (excl.includes("medium") && excl.includes("high")) {
              setCostFilter("under25");
            } else if (excl.includes("high")) {
              setCostFilter("under75");
            }
          }

          if (parsed.timeframe?.start) {
            const today = todayISO_EST();
            const tomorrow = tomorrowISO_EST();
            if (parsed.timeframe.start === today && parsed.timeframe.end === today) {
              setTimeframeFilter("today");
            } else if (parsed.timeframe.start === tomorrow && parsed.timeframe.end === tomorrow) {
              setTimeframeFilter("tomorrow");
            } else {
              const { start: satStr, end: sunStr } = getWeekendDates();
              if (parsed.timeframe.start === satStr && parsed.timeframe.end === sunStr) {
                setTimeframeFilter("weekend");
              } else {
                setTimeframeFilter("custom");
                setCustomDateRange({
                  from: new Date(parsed.timeframe.start + "T12:00:00"),
                  to: parsed.timeframe.end ? new Date(parsed.timeframe.end + "T12:00:00") : undefined,
                });
                setDraftDateRange({
                  from: new Date(parsed.timeframe.start + "T12:00:00"),
                  to: parsed.timeframe.end ? new Date(parsed.timeframe.end + "T12:00:00") : undefined,
                });
              }
            }
          }
        } catch { /* noop */ }
      }
      const savedStored = sessionStorage.getItem("rp-saved");
      if (savedStored) {
        try { setSavedIds(new Set(JSON.parse(savedStored))); } catch { /* noop */ }
      }
    }
  }, []);

  const effectiveDateRange = useMemo(() => {
    const today = todayISO_EST();
    const tomorrow = tomorrowISO_EST();

    if (timeframeFilter === "today") return { start: today, end: today };
    if (timeframeFilter === "tomorrow") return { start: tomorrow, end: tomorrow };
    if (timeframeFilter === "weekend") {
      return getWeekendDates();
    }
    if (timeframeFilter === "custom" && customDateRange.from) {
      // Use local Y/M/D from the picker (calendar shows local days), avoid UTC drift.
      const toLocalISO = (d: Date) =>
        toISODate(d.getFullYear(), d.getMonth() + 1, d.getDate());
      const start = toLocalISO(customDateRange.from);
      const end = customDateRange.to ? toLocalISO(customDateRange.to) : start;
      return { start, end };
    }
    if (wizardData?.timeframe?.start) {
      return { start: wizardData.timeframe.start, end: wizardData.timeframe.end || wizardData.timeframe.start };
    }
    return { start: today, end: today };
  }, [timeframeFilter, customDateRange, wizardData]);

  const dayCount = useMemo(() => {
    const s = new Date(effectiveDateRange.start + "T12:00:00");
    const e = new Date(effectiveDateRange.end + "T12:00:00");
    return Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
  }, [effectiveDateRange]);

  // Weather date label
  const weatherDateLabel = useMemo(() => {
    if (dayCount === 1) return formatDateLabel(effectiveDateRange.start);
    return `${formatDateLabel(effectiveDateRange.start)} – ${formatDateLabel(effectiveDateRange.end)}`;
  }, [effectiveDateRange, dayCount]);
  const ResultsWeatherIcon = getWeatherIcon(weather?.isRainy ? "Rain" : weather?.days[0]?.condition);

  useEffect(() => {
    async function fetchWeather() {
      setWeatherLoading(true);
      try {
        const forecast = await getWeatherForecast({ data: { startDate: effectiveDateRange.start, endDate: effectiveDateRange.end } });
        // Only treat as valid weather if we got at least one day with finite temps.
        // (Far-future dates fall outside the NWS 7-day window and yield empty days.)
        const hasUsableDays =
          forecast?.days?.length > 0 &&
          forecast.days.every((d) => Number.isFinite(d.tempHigh) && Number.isFinite(d.tempLow));
        setWeather(hasUsableDays ? forecast : null);
      } catch {
        setWeather(null);
      }
      setWeatherLoading(false);
    }
    fetchWeather();
  }, [effectiveDateRange]);

  useEffect(() => {
    async function fetchEvents() {
      setLoading(true);
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("is_active", true)
        .is("parent_id", null)
        .order("novelty_score", { ascending: false });

      if (!error && data) {
        setEvents(data as unknown as DBEvent[]);
      }
      setLoading(false);
    }
    fetchEvents();
  }, []);

  // Prefetch ZIP centroids for events without venue lat/lng so drive-time
  // bubbles reflect the user's actual origin instead of falling back to the
  // LWR baseline. Hits the public zip_cache first, then fills any gaps via
  // geocodeZip (which itself caches into zip_cache).
  useEffect(() => {
    if (events.length === 0) return;
    const needed = new Set<string>();
    for (const e of events) {
      if (typeof e.lat === "number" && typeof e.lng === "number") continue;
      const zip = extractZipFromAddress(e.venue_address);
      if (zip) needed.add(zip);
    }
    if (needed.size === 0) return;

    let cancelled = false;
    (async () => {
      const next = new Map<string, LatLng>();
      const zips = [...needed];
      // 1. Bulk read from cache.
      try {
        const { data } = await supabase
          .from("zip_cache")
          .select("zip, lat, lng")
          .in("zip", zips);
        if (data) {
          for (const row of data) {
            next.set(row.zip, { lat: row.lat, lng: row.lng });
          }
        }
      } catch {
        /* fall through to per-ZIP geocode */
      }
      if (!cancelled && next.size > 0) setZipCoords(new Map(next));

      // 2. Geocode the rest (best-effort, parallel, throttled by Promise.all).
      const missing = zips.filter((z) => !next.has(z));
      if (missing.length > 0) {
        const results = await Promise.all(
          missing.map((zip) =>
            geocodeZip({ data: { zip } }).catch(() => null),
          ),
        );
        for (let i = 0; i < missing.length; i++) {
          const r = results[i];
          if (r && r.ok) next.set(missing[i], { lat: r.lat, lng: r.lng });
        }
        if (!cancelled) setZipCoords(new Map(next));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [events]);

  const toggleSave = (id: string) => {
    setSavedIds((prev) => {
      const next = new Set(prev);
      const wasSaved = next.has(id);
      if (wasSaved) next.delete(id);
      else {
        next.add(id);
        bumpExploreActivity("save");
        // Only increment save_count once per session per event
        if (typeof window !== "undefined") {
          const tracked: string[] = JSON.parse(sessionStorage.getItem("rp-save-tracked") || "[]");
          if (!tracked.includes(id)) {
            fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/increment_event_stat`, {
              method: "POST",
              headers: { "Content-Type": "application/json", apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY, Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
              body: JSON.stringify({ event_id: id, stat_column: "save_count" }),
            }).catch(() => {});
            tracked.push(id);
            sessionStorage.setItem("rp-save-tracked", JSON.stringify(tracked));
          }
        }
      }
      if (typeof window !== "undefined") {
        sessionStorage.setItem("rp-saved", JSON.stringify([...next]));
      }
      return next;
    });
  };

  const trackLinkClick = (id: string) => {
    bumpExploreActivity("open");
    fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/increment_event_stat`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY, Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
      body: JSON.stringify({ event_id: id, stat_column: "link_click_count" }),
    }).catch(() => {});
  };

  const categories = useMemo(() => [...new Set(events.map((e) => e.category))].sort(), [events]);

  const getWeekdayIndex = (): number | null => {
    if (timeframeFilter === "today") return weekdayIndexForISO(todayISO_EST());
    if (timeframeFilter === "tomorrow") return weekdayIndexForISO(tomorrowISO_EST());
    return null;
  };

  const getWeekdayIndicesForRange = (start: string, end: string): number[] => {
    const result: number[] = [];
    const d = new Date(start + "T12:00:00");
    const endD = new Date(end + "T12:00:00");
    while (d <= endD) {
      result.push(d.getDay());
      d.setDate(d.getDate() + 1);
    }
    return result;
  };

  const extraSignals = useMemo(() => {
    if (!wizardData?.localPasses) return null;
    return parseExtras(wizardData.localPasses);
  }, [wizardData]);

  // Neutral intensity baseline — the wizard no longer collects age, so
  // kid/intensity preferences come from the extras free-text instead.
  const inferredIntensity = 5;

  // Origin point for distance math. If the user gave us a ZIP in the wizard,
  // anchor on their lat/lng; otherwise fall back to the Lakewood Ranch baseline
  // so existing drive_minutes_from_lwr values remain meaningful.
  const userOrigin: LatLng = useMemo(() => {
    const o = wizardData?.origin;
    if (o && typeof o.lat === "number" && typeof o.lng === "number") {
      return { lat: o.lat, lng: o.lng };
    }
    return LWR_CENTER;
  }, [wizardData?.origin?.lat, wizardData?.origin?.lng]);

  const hasUserOrigin = !!wizardData?.origin;
  const originLabel = wizardData?.origin?.zip ?? "Lakewood Ranch";
  const driveTooltip = hasUserOrigin
    ? `Estimated drive time from ${originLabel}`
    : "Estimated drive time from Lakewood Ranch";

  const driveFor = (e: DBEvent) => driveMinutesFor(e, userOrigin, zipCoords);

  // Dynamic subtitle based on active timeframe
  const timeframeSubtitle = useMemo(() => {
    if (timeframeFilter === "today") return "Today's";
    if (timeframeFilter === "tomorrow") return "Tomorrow's";
    if (timeframeFilter === "weekend") return "Weekend";
    if (timeframeFilter === "custom" && customDateRange.from) {
      return format(customDateRange.from, "MMM d") + (customDateRange.to && customDateRange.to.getTime() !== customDateRange.from.getTime() ? `–${format(customDateRange.to, "MMM d")}` : "");
    }
    return "Weekend";
  }, [timeframeFilter, customDateRange]);

  const filtered = useMemo(() => {
    let list = [...events];

    // Personalization helpers (signed-in only).
    // - Novelty boost: never-visited +15, last visit >12mo +8
    // - Recency penalty: visited within 30d -25
    // - Affinity boost: +5 per (rating-3) on same category (sum across all of
    //   that user's ratings, capped at ±20). E.g. two 5-star Beach picks =
    //   +20 to other Beach events; a 1-star bowling rating = -10 to Bowling.
    const today = todayISO_EST();
    const todayMs = new Date(today + "T12:00:00").getTime();
    const categoryAffinity = new Map<string, number>();
    const ratingsSnap = ratingsMapRef.current;
    const visitsSnap = visitsMapRef.current;
    if (user && ratingsSnap.size > 0) {
      for (const [eid, rating] of ratingsSnap) {
        const ev = events.find((e) => e.id === eid);
        if (!ev) continue;
        const cat = ev.category.toLowerCase();
        categoryAffinity.set(cat, (categoryAffinity.get(cat) ?? 0) + (rating - 3) * 5);
      }
    }
    const personalDelta = (e: DBEvent): number => {
      if (!user) return 0;
      let s = 0;
      const lastVisit = visitsSnap.get(e.id);
      if (!lastVisit) {
        s += 15; // never visited — novelty for this user
      } else {
        const daysAgo = Math.floor((todayMs - new Date(lastVisit + "T12:00:00").getTime()) / 86_400_000);
        if (daysAgo < 30) s -= 25;
        else if (daysAgo > 365) s += 8;
      }
      const cat = e.category.toLowerCase();
      const aff = categoryAffinity.get(cat);
      if (typeof aff === "number") s += Math.max(-20, Math.min(20, aff));
      return s;
    };

    // === HARD FILTER: $150 cap ===
    list = list.filter((e) => {
      const cost = Math.min(parseCostNumeric(e.cost_level), parseCostNumeric(e.cost_details));
      return cost <= 150 || cost === 999;
    });

    // === Cost filter ===
    if (costFilter === "free") {
      // Free means: cost_level says free AND cost_details has no real $ price
      // (allow "free entry" / "free beach" / "grounds free" with optional paid extras)
      list = list.filter((e) => {
        const levelFree = /free/i.test(e.cost_level) || parseCostNumeric(e.cost_level) === 0;
        if (!levelFree) return false;
        const details = e.cost_details || "";
        if (!/\$[0-9]/.test(details)) return true;
        return /free\s+(entry|beach|grounds|admission)|grounds\s+free/i.test(details);
      });
    } else if (costFilter === "under25") {
      list = list.filter((e) => {
        const cost = Math.min(parseCostNumeric(e.cost_level), parseCostNumeric(e.cost_details));
        return cost <= 25;
      });
    } else if (costFilter === "under75") {
      list = list.filter((e) => {
        const cost = Math.min(parseCostNumeric(e.cost_level), parseCostNumeric(e.cost_details));
        return cost <= 75;
      });
    }

    if (categoryFilter !== "all") list = list.filter((e) => e.category === categoryFilter);
    if (settingFilter !== "all") list = list.filter((e) => e.indoor_outdoor === settingFilter || e.indoor_outdoor === "both");

    if (distanceFilter !== "all") {
      const maxDist = parseInt(distanceFilter, 10);
      list = list.filter((e) => driveFor(e) <= maxDist);
    }

    // Hard cap from the wizard's "max drive" answer — unless the user clicked
    // "show more" to relax it for this view.
    if (hasUserOrigin && !showBeyondCap) {
      const cap = wizardData?.origin?.maxDriveMinutes;
      if (typeof cap === "number" && cap > 0) {
        list = list.filter((e) => driveFor(e) <= cap);
      }
    }

    // Duration filter — match user's time-available bucket against estimated_duration
    if (durationFilter !== "any") {
      list = list.filter((e) => matchesDurationPreference(e.estimated_duration, durationFilter));
    }

    // Timeframe filtering — parse schedule_notes into weekday indices so we
    // correctly handle abbreviations ("Mon-Sat"), ranges ("Thu–Sun"), and
    // prose ("Dawn to dusk", "24 hours", "Always open"). Unparseable notes
    // default to "always open" — better to over-include than wrongly exclude.
    const weekdayIndices = getWeekdayIndicesForRange(effectiveDateRange.start, effectiveDateRange.end);
    if (timeframeFilter === "today" || timeframeFilter === "tomorrow") {
      const wd = getWeekdayIndex();
      if (wd !== null) {
        list = list.filter((e) => {
          if (e.start_date) return true; // date-specific events handled below
          return isOpenOnWeekday(e.schedule_notes, wd);
        });
      }
    } else if (timeframeFilter === "weekend" || (timeframeFilter === "custom" && customDateRange.from)) {
      list = list.filter((e) => {
        if (e.start_date) return true; // date-specific events handled below
        return isOpenOnAnyWeekday(e.schedule_notes, weekdayIndices);
      });
    }

    // Date-specific events: use start_date/end_date range overlap
    list = list.filter((e) => {
      if (!e.start_date) return true; // recurring/anytime events always pass
      const endDate = e.end_date || e.start_date;
      return e.start_date <= effectiveDateRange.end && endDate >= effectiveDateRange.start;
    });

    // Specific play-date list (e.g. spring-training game days encoded in
    // schedule_notes). When present, require at least one of those dates
    // to fall inside the user's selected window — otherwise the venue is
    // "dark" on every day they're considering.
    list = list.filter((e) => {
      if (!hasSpecificDates(e.schedule_notes)) return true;
      const dates = extractSpecificDates(e.schedule_notes);
      return matchesSelectedWindow(dates, effectiveDateRange.start, effectiveDateRange.end);
    });

    // Search query filter — matches title, venue name, category, and description
    // so a user typing "kayak" finds Robinson Preserve etc., not just titles.
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((e) => {
        const hay = `${e.title} ${e.venue_name} ${e.category} ${e.description}`.toLowerCase();
        return hay.includes(q);
      });
    }

    // === RANKING ===
    // Smart Ranked: weights novelty + personal fit (wizard answers, age, weather, popularity).
    // Shake It Up: heavy novelty + timeliness + randomization; popularity capped so the same
    // few "saved" venues don't always win.
    const isShuffleMode = rankMode === "shuffle";

    // Helper: does this event's date range fall inside the currently-selected window?
    const inSelectedWindow = (e: DBEvent) => {
      // Specific-date list takes precedence: only "in window" if a play date hits.
      if (hasSpecificDates(e.schedule_notes)) {
        return matchesSelectedWindow(
          extractSpecificDates(e.schedule_notes),
          effectiveDateRange.start,
          effectiveDateRange.end,
        );
      }
      if (!e.start_date) return false;
      const endDate = e.end_date || e.start_date;
      return e.start_date <= effectiveDateRange.end && endDate >= effectiveDateRange.start;
    };

    list.sort((a, b) => {
      // Novelty matters more in Shake It Up — it's the whole point of "fresh mix".
      let scoreA = a.novelty_score * (isShuffleMode ? 8 : 5);
      let scoreB = b.novelty_score * (isShuffleMode ? 8 : 5);

      if (weather) {
        // Gradient weather scoring — Florida storms are usually short, so a
        // 50–60% chance shouldn't crowd out outdoor picks. Only push hard when
        // rain is near-certain (80%+). Below 30% we lean slightly outdoor.
        //
        // Resilience-aware: an outdoor activity's penalty scales with its
        // weather_sensitivity (0 = bulletproof, 100 = ruined by drizzle).
        // Low-resilience picks get downranked on a steeper curve than rugged
        // ones — sensitivity^1.6 means a sensitivity-90 venue is hit ~3.5×
        // harder than a sensitivity-50 venue at the same rain probability.
        // The same curve applies (more gently) to "both" venues.
        const pop = weather.precipChance; // 0-100, avg across selected days
        let indoorBoost = 0;
        let outdoorBoost = 0;
        let sensitivityPenaltyMult = 0; // multiplied by sensitivity^1.6 / 100^0.6
        if (pop >= 85) {
          indoorBoost = 22;
          outdoorBoost = -6;
          sensitivityPenaltyMult = 0.55;
        } else if (pop >= 70) {
          indoorBoost = 14;
          outdoorBoost = -3;
          sensitivityPenaltyMult = 0.35;
        } else if (pop >= 50) {
          indoorBoost = 6;
          outdoorBoost = -1;
          sensitivityPenaltyMult = 0.18;
        } else if (pop < 30) {
          outdoorBoost = 6;
        }
        // Exponent makes high-sensitivity (low-resilience) picks fall faster.
        const sensCurve = (sens: number) =>
          Math.pow(Math.max(0, Math.min(100, sens)), 1.6) / Math.pow(100, 0.6);
        const applyWeather = (e: DBEvent) => {
          let s = 0;
          if (e.indoor_outdoor === "indoor") s += indoorBoost;
          else if (e.indoor_outdoor === "outdoor") {
            s += outdoorBoost;
            s -= sensCurve(e.weather_sensitivity) * sensitivityPenaltyMult;
          } else if (e.indoor_outdoor === "both") {
            // Covered/mixed venues — partial boost when wet, gentler sensitivity hit.
            if (pop >= 50) s += Math.round(indoorBoost * 0.5);
            s -= sensCurve(e.weather_sensitivity) * sensitivityPenaltyMult * 0.35;
          }
          return s;
        };
        scoreA += applyWeather(a);
        scoreB += applyWeather(b);
        if (isShuffleMode) {
          // Average across the whole window, not just day 1, so a multi-day
          // pick reflects the actual heat the user will feel.
          const days = weather.days;
          const avgTemp = days.length > 0
            ? days.reduce((s, d) => s + (d.tempHigh + d.tempLow) / 2, 0) / days.length
            : 75;
          if (avgTemp > 85) {
            if (a.indoor_outdoor === "indoor" || a.category.toLowerCase().includes("water")) scoreA += 15;
            if (b.indoor_outdoor === "indoor" || b.category.toLowerCase().includes("water")) scoreB += 15;
          }
        }
      }

      // User's stated weather-exposure preference (from wizard).
      // -2 = strong indoor, +2 = strong outdoor. Scales linearly.
      const exposurePref = wizardData?.weatherExposure ?? 0;
      if (exposurePref !== 0) {
        const w = Math.abs(exposurePref) * 8; // 8..16 weight
        const wantOutdoor = exposurePref > 0;
        const applyExposure = (e: DBEvent) => {
          let s = 0;
          if (wantOutdoor) {
            if (e.indoor_outdoor === "outdoor") s += w;
            else if (e.indoor_outdoor === "indoor") s -= w;
            if (e.indoor_outdoor === "outdoor") s += (e.weather_sensitivity / 100) * w * 0.5;
          } else {
            if (e.indoor_outdoor === "indoor") s += w;
            else if (e.indoor_outdoor === "outdoor") s -= w;
            if (e.indoor_outdoor === "outdoor") s -= (e.weather_sensitivity / 100) * w * 0.5;
          }
          return s;
        };
        scoreA += applyExposure(a);
        scoreB += applyExposure(b);
      }

      scoreA -= Math.abs(a.intensity - inferredIntensity) * 4;
      scoreB -= Math.abs(b.intensity - inferredIntensity) * 4;

      // Popularity boost — capped lower in shuffle so a single high-save event
      // (e.g. Myakka Canopy Walkway) doesn't always sit on top.
      const popCap = isShuffleMode ? 4 : 10;
      const clickCap = isShuffleMode ? 2 : 5;
      scoreA += Math.min((a as any).save_count * (isShuffleMode ? 1 : 2), popCap);
      scoreB += Math.min((b as any).save_count * (isShuffleMode ? 1 : 2), popCap);
      scoreA += Math.min((a as any).link_click_count * (isShuffleMode ? 0.25 : 0.5), clickCap);
      scoreB += Math.min((b as any).link_click_count * (isShuffleMode ? 0.25 : 0.5), clickCap);

      if (costFilter === "free" || costFilter === "under25") {
        const costA = Math.min(parseCostNumeric(a.cost_level), parseCostNumeric(a.cost_details));
        const costB = Math.min(parseCostNumeric(b.cost_level), parseCostNumeric(b.cost_details));
        if (costA === 0) scoreA += 15;
        if (costB === 0) scoreB += 15;
        scoreA -= costA * 0.3;
        scoreB -= costB * 0.3;
      }

      const driveA = driveFor(a);
      const driveB = driveFor(b);
      // Linear penalty up to 45 min, then a quadratic ramp so a 90-min drive
      // really does feel costly relative to closer picks.
      const drivePenalty = (m: number) =>
        m <= 45 ? m * 0.3 : 45 * 0.3 + Math.pow(m - 45, 1.5) * 0.4;
      scoreA -= drivePenalty(driveA);
      scoreB -= drivePenalty(driveB);

      // Drive-aware duration nudge — if round-trip drive plus the venue's own
      // duration overshoots the user's "time available" bucket, downrank
      // (soft, not a hard filter — matchesDurationPreference still gates on
      // venue duration alone).
      if (durationFilter !== "any") {
        const ceiling = durationFilter === "short" ? 2 : durationFilter === "half" ? 5 : 10;
        const penalty = durationFilter === "full" ? 4 : 8;
        const commit = (e: DBEvent, drive: number) =>
          parseDurationHours(e.estimated_duration).max + (drive * 2) / 60;
        if (commit(a, driveA) > ceiling) scoreA -= penalty;
        if (commit(b, driveB) > ceiling) scoreB -= penalty;
      }

      // Timeliness — scarcity-scaled boost. Short, novel, in-window events surface
      // strongly; long seasons (e.g. MiLB regular season) get only a small nudge so
      // they compete with ongoing events on fit and novelty.
      const runLengthDays = (e: DBEvent): number => {
        // For specific-date venues, "run length" = number of in-window play
        // dates. A single in-window game day is as scarce as a one-day event.
        if (hasSpecificDates(e.schedule_notes)) {
          const inW = datesInRange(
            extractSpecificDates(e.schedule_notes),
            effectiveDateRange.start,
            effectiveDateRange.end,
          ).length;
          return Math.max(1, inW);
        }
        if (!e.start_date) return Infinity;
        const start = new Date(e.start_date).getTime();
        const end = new Date(e.end_date || e.start_date).getTime();
        return Math.max(1, Math.round((end - start) / 86_400_000) + 1);
      };
      const scarcityWeight = (days: number): number => {
        if (days <= 14) return 1.0;
        if (days <= 30) return 0.6;
        if (days <= 90) return 0.35;
        return 0.15;
      };
      const timelinessBoost = (e: DBEvent, inWindow: boolean): number => {
        if (!e.start_date && !hasSpecificDates(e.schedule_notes)) return 0;
        const days = runLengthDays(e);
        const scarcity = scarcityWeight(days);
        const noveltyMod = 0.5 + (e.novelty_score / 10) * 0.5;
        const baseInWindow = isShuffleMode ? 30 : 22;
        const baseDated = isShuffleMode ? 6 : 4;
        const base = inWindow ? baseInWindow : baseDated;
        let boost = base * scarcity * noveltyMod;
        // One-day-in-window bonus — applies to true one-off dated events AND
        // to specific-date venues with exactly one play date in the window.
        if (inWindow && days <= 1 && (!e.schedule_notes || hasSpecificDates(e.schedule_notes))) boost += 6;
        return boost;
      };
      scoreA += timelinessBoost(a, inSelectedWindow(a));
      scoreB += timelinessBoost(b, inSelectedWindow(b));

      if (extraSignals) {
        const fieldsA = `${a.title} ${a.description} ${a.venue_name} ${a.category}`.toLowerCase();
        const fieldsB = `${b.title} ${b.description} ${b.venue_name} ${b.category}`.toLowerCase();

        for (const kw of extraSignals.boostKeywords) {
          if (fieldsA.includes(kw)) scoreA += 25;
          if (fieldsB.includes(kw)) scoreB += 25;
        }
        for (const kw of extraSignals.penalizeKeywords) {
          if (fieldsA.includes(kw)) scoreA -= 30;
          if (fieldsB.includes(kw)) scoreB -= 30;
        }

        const catA = a.category.toLowerCase();
        const catB = b.category.toLowerCase();
        for (const cat of extraSignals.boostCategories) {
          if (catA.includes(cat.toLowerCase())) scoreA += 15;
          if (catB.includes(cat.toLowerCase())) scoreB += 15;
        }
        for (const cat of extraSignals.penalizeCategories) {
          if (catA.includes(cat.toLowerCase())) scoreA -= 25;
          if (catB.includes(cat.toLowerCase())) scoreB -= 25;
        }

        if (extraSignals.boostOutdoor) {
          if (a.indoor_outdoor === "outdoor" || a.indoor_outdoor === "both") scoreA += 10;
          if (b.indoor_outdoor === "outdoor" || b.indoor_outdoor === "both") scoreB += 10;
        }
        if (extraSignals.boostIndoor) {
          if (a.indoor_outdoor === "indoor" || a.indoor_outdoor === "both") scoreA += 10;
          if (b.indoor_outdoor === "indoor" || b.indoor_outdoor === "both") scoreB += 10;
        }

        for (const mem of extraSignals.hasMembership) {
          const memLower = mem.toLowerCase();
          if (fieldsA.includes(memLower)) scoreA += 8;
          if (fieldsB.includes(memLower)) scoreB += 8;
        }

        if (extraSignals.reciprocalMatches.size > 0) {
          for (const [venue] of extraSignals.reciprocalMatches) {
            if (fieldsA.includes(venue)) scoreA += 20;
            if (fieldsB.includes(venue)) scoreB += 20;
          }
        }
      }

      // In shuffle mode, add real randomization (±25) so the same event
      // doesn't keep landing first across shakes.
      if (isShuffleMode) {
        // Per-event hash mixed with the run's seed → stable within a shake, fresh across shakes.
        const seedInt = Math.floor(randomSeed * 1000000);
        const hashA = ((a.id.charCodeAt(0) * 31 + a.id.charCodeAt(5) * 17 + seedInt) >>> 0) % 50 - 25;
        const hashB = ((b.id.charCodeAt(0) * 31 + b.id.charCodeAt(5) * 17 + seedInt) >>> 0) % 50 - 25;
        scoreA += hashA;
        scoreB += hashB;
      }

      scoreA += personalDelta(a);
      scoreB += personalDelta(b);

      return scoreB - scoreA;
    });
    // Cap at 30 results
    return list.slice(0, 30);
  }, [events, categoryFilter, settingFilter, distanceFilter, costFilter, durationFilter, timeframeFilter, customDateRange, weather, wizardData, extraSignals, effectiveDateRange, inferredIntensity, searchQuery, randomSeed, rankMode, userOrigin, hasUserOrigin, showBeyondCap, user]);

  const activeFilterCount = [categoryFilter, settingFilter, distanceFilter, costFilter].filter((f) => f !== "all").length + (durationFilter !== "any" ? 1 : 0);

  // Track whether the user has explicitly chosen Shake It Up. If they have,
  // don't auto-flip them back to Smart when they apply a filter.
  const [shakeStuck, setShakeStuck] = useState(false);

  // Auto-switch to Smart Ranked when any filter is active — unless the user
  // explicitly picked Shake It Up since their last filter change.
  useEffect(() => {
    if (activeFilterCount > 0 && rankMode !== "smart" && !shakeStuck) {
      setRankMode("smart");
    }
  }, [activeFilterCount, shakeStuck]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBuildItinerary = () => {
    if (typeof window !== "undefined") {
      const saved = events.filter((e) => savedIds.has(e.id));
      sessionStorage.setItem("rp-itinerary", JSON.stringify(saved));
    }
    navigate({ to: "/plan" });
  };

  const handleCustomDateSelect = (range: { from?: Date; to?: Date } | undefined) => {
    if (!range) return;
    setCustomDateRange(range);
  };

  const handleCustomDateDayClick = (date: Date) => {
    const clickedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (!draftDateRange.from || draftDateRange.to) {
      setDraftDateRange({ from: clickedDate, to: undefined });
      return;
    }

    const from = new Date(draftDateRange.from.getFullYear(), draftDateRange.from.getMonth(), draftDateRange.from.getDate());
    const [start, end] = clickedDate.getTime() < from.getTime()
      ? [clickedDate, from]
      : [from, clickedDate];

    const nextRange = { from: start, to: end };
    setDraftDateRange(nextRange);
    handleCustomDateSelect(nextRange);
    setTimeframeFilter("custom");
    setShowDatePicker(false);
  };

  const getReciprocalBenefit = (event: DBEvent): string | null => {
    if (!extraSignals?.reciprocalMatches?.size) return null;
    const titleLower = event.title.toLowerCase();
    const venueLower = event.venue_name.toLowerCase();
    for (const [venue, benefit] of extraSignals.reciprocalMatches) {
      if (titleLower.includes(venue) || venueLower.includes(venue)) return benefit;
    }
    return null;
  };

  const getScheduleBadge = (event: DBEvent): { label: string; variant: "limited" | "day" | "anytime" } => {
    // Specific-date list (game-day-only venues, etc.) wins over season range.
    if (hasSpecificDates(event.schedule_notes)) {
      const all = extractSpecificDates(event.schedule_notes);
      const inWindow = datesInRange(all, effectiveDateRange.start, effectiveDateRange.end);
      if (inWindow.length === 1) {
        const d = new Date(inWindow[0] + "T12:00:00");
        return { label: format(d, "MMM d, yyyy"), variant: "limited" };
      }
      if (inWindow.length > 1) {
        return { label: `${inWindow.length} dates in range`, variant: "limited" };
      }
      // Filter prevents this branch in practice, but stay safe.
      return { label: "Check schedule", variant: "limited" };
    }

    // Date-specific events: single date or range
    if (event.start_date) {
      const s = new Date(event.start_date + "T12:00:00");
      if (event.end_date && event.end_date !== event.start_date) {
        const e = new Date(event.end_date + "T12:00:00");
        return { label: `${format(s, "MMM d")}–${format(e, "MMM d, yyyy")}`, variant: "limited" };
      }
      return { label: format(s, "MMM d, yyyy"), variant: "limited" };
    }

    const notes = (event.schedule_notes || "").trim();
    if (!notes) return { label: "Daily", variant: "anytime" };

    const lower = notes.toLowerCase();

    // Days of week extraction (run BEFORE Daily shortcut so "Every Saturday year-round" → Sat)
    const dayMap: Record<string, string> = {
      mon: "Mon", monday: "Mon",
      tue: "Tue", tues: "Tue", tuesday: "Tue",
      wed: "Wed", weds: "Wed", wednesday: "Wed",
      thu: "Thu", thur: "Thu", thurs: "Thu", thursday: "Thu",
      fri: "Fri", friday: "Fri",
      sat: "Sat", saturday: "Sat",
      sun: "Sun", sunday: "Sun",
    };
    const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    // Match ranges like "Mon-Sat" or "Mon–Fri"
    const rangeMatch = lower.match(/\b(mon(?:day)?|tue(?:s|sday)?|wed(?:s|nesday)?|thu(?:r|rs|rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)\s*[-–—to ]+\s*(mon(?:day)?|tue(?:s|sday)?|wed(?:s|nesday)?|thu(?:r|rs|rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)\b/);
    if (rangeMatch) {
      const a = dayMap[rangeMatch[1]];
      const b = dayMap[rangeMatch[2]];
      if (a && b) return { label: `${a}–${b}`, variant: "day" };
    }

    // Match individual day mentions
    const found = new Set<string>();
    for (const key of Object.keys(dayMap)) {
      const re = new RegExp(`\\b${key}\\b`, "i");
      if (re.test(lower)) found.add(dayMap[key]);
    }
    if (found.size > 0) {
      const sorted = order.filter((d) => found.has(d));
      return { label: sorted.join(", "), variant: "day" };
    }

    // Weekend / weekday shorthand
    if (/\bweekends?\b/.test(lower)) return { label: "Sat–Sun", variant: "day" };
    if (/\bweekdays?\b/.test(lower)) return { label: "Mon–Fri", variant: "day" };

    // Daily / always open (only if no specific days were mentioned)
    if (/\b(daily|every\s*day|always|year-?round|dawn\s*to\s*dusk|24\/7|anytime|open\s*daily)\b/.test(lower)) {
      return { label: "Daily", variant: "anytime" };
    }

    // Too complicated to summarize concisely
    return { label: "Varies", variant: "day" };
  };


  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen pt-6 md:pt-10 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header — title + neighborly tagline */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
            <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-none" style={{ fontFamily: "'Syne', sans-serif" }}>
              Your Picks<span className="text-highlight">.</span>
            </h1>
            <p
              className="mt-1 text-xl md:text-2xl text-foreground"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              {rankMode === "shuffle"
                ? wizardData
                  ? "A fresh mix — your preferences gently considered."
                  : "A fresh mix of local spots — shake it up for more."
                : wizardData
                  ? "Local spots matched to your crew, your vibe, your weekend."
                  : "Ranked by what locals love most + what's happening soon."}
            </p>
          </motion.div>

          {/* Timeframe segmented control + weather pill — same row, same height */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="flex items-center gap-2 mb-3"
          >
            <div role="group" aria-label="Timeframe" className="inline-flex items-center bg-muted/60 rounded-full p-0.5 h-9">
              {[
                { key: "today", label: "Today" },
                { key: "tomorrow", label: "Tomorrow" },
                { key: "weekend", label: "Weekend" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => { setTimeframeFilter(t.key); setShowDatePicker(false); if (!wizardData) setRankMode("shuffle"); }}
                  aria-pressed={timeframeFilter === t.key}
                  className={`h-8 px-3.5 rounded-full text-xs font-semibold transition-all inline-flex items-center ${
                    timeframeFilter === t.key ? "text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                  style={timeframeFilter === t.key ? { background: "var(--color-primary)" } : undefined}
                >
                  {t.label}
                </button>
              ))}
              <Popover open={showDatePicker} onOpenChange={(open) => { setShowDatePicker(open); if (open) setDraftDateRange(customDateRange); }}>
                <PopoverTrigger asChild>
                  <button
                    className={`h-8 px-2.5 rounded-full text-xs font-semibold transition-all inline-flex items-center gap-1 ${
                      timeframeFilter === "custom" ? "text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
                    }`}
                    style={timeframeFilter === "custom" ? { background: "var(--color-primary)" } : undefined}
                    aria-label="Custom date"
                  >
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {timeframeFilter === "custom" && customDateRange.from && (
                      <span className="hidden sm:inline">
                        {format(customDateRange.from, "MMM d")}{customDateRange.to && customDateRange.to.getTime() !== customDateRange.from.getTime() ? `–${format(customDateRange.to, "MMM d")}` : ""}
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="p-3 border-b border-border">
                    <p className="text-xs text-muted-foreground">Tap start date, then end date</p>
                  </div>
                  <Calendar mode="range" min={1} selected={draftDateRange as any} onDayClick={handleCustomDateDayClick}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus className={cn("p-3 pointer-events-auto")} />
                  {draftDateRange.from && !draftDateRange.to && (
                    <div className="px-3 pb-3">
                      <button onClick={() => {
                        const d = draftDateRange.from!;
                        const nextRange = { from: d, to: d };
                        setDraftDateRange(nextRange);
                        handleCustomDateSelect(nextRange);
                        setTimeframeFilter("custom");
                        setShowDatePicker(false);
                      }} className="w-full text-center text-sm font-medium text-primary hover:underline">
                        Just {format(draftDateRange.from, "MMM d")} →
                      </button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>

            {/* Weather pill — same height, sits next to timeframe */}
            {weather && !weatherLoading && (
              <div
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border shrink-0"
                style={{
                  background: "color-mix(in srgb, var(--color-accent) 10%, transparent)",
                  borderColor: "color-mix(in srgb, var(--color-accent) 25%, transparent)",
                }}
              >
                <ResultsWeatherIcon className="h-4 w-4 shrink-0 text-accent" />
                <span className="font-semibold text-xs sm:text-sm text-accent">
                  {weather.days.length === 1
                    ? `${weather.days[0].tempHigh}°/${weather.days[0].tempLow}°`
                    : `${Math.max(...weather.days.map(d => d.tempHigh))}° hi`}
                </span>
                <span className="hidden sm:inline text-xs text-accent/75">
                  · {weather.days.length === 1 ? `${weather.days[0].precipChance}%` : `${Math.round(weather.precipChance)}%`} rain
                </span>
              </div>
            )}
          </motion.div>

          {/* Control row — filter + collapsible search + rank toggle */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
            <div className="flex items-center gap-2 flex-nowrap">
              <button onClick={() => setShowFilters(!showFilters)}
                className={`relative inline-flex items-center justify-center h-9 w-9 rounded-full border transition-all shrink-0 ${
                  activeFilterCount > 0
                    ? "border-primary bg-primary/10 text-primary"
                    : showFilters
                      ? "border-foreground/30 text-foreground"
                      : "border-border hover:border-primary/40 text-muted-foreground hover:text-foreground"
                }`}
                aria-label="Filters"
              >
                <Filter className="h-3.5 w-3.5" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Search — icon-only on mobile, expands to full field when tapped */}
              <div className={`${searchOpen ? "flex-1 min-w-0" : "shrink-0"}`}>
                {searchOpen ? (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search…"
                      aria-label="Search activities"
                      autoFocus
                      maxLength={100}
                      onBlur={() => setSearchOpen(false)}
                      className="w-full pl-9 pr-8 py-2 rounded-full text-sm border border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder:text-muted-foreground"
                    />
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setSearchQuery(""); setSearchOpen(false); }}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Close search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setSearchOpen(true)}
                    className={`inline-flex items-center justify-center h-9 w-9 rounded-full border transition-all ${
                      searchQuery
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/40 text-muted-foreground hover:text-foreground"
                    }`}
                    aria-label="Search"
                  >
                    <Search className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Rank mode toggle */}
              {!searchOpen && (
                <div role="group" aria-label="Ranking mode" className="ml-auto inline-flex items-center gap-1 bg-muted/60 rounded-full p-0.5 shrink-0">
                  <motion.button
                    whileTap={{ rotate: 180, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                    onClick={() => {
                      setRankMode("shuffle");
                      setShakeStuck(true);
                      setRandomSeed(Math.random());
                      setCategoryFilter("all");
                      setSettingFilter("all");
                      setDistanceFilter("all");
                      setCostFilter("all");
                      setDurationFilter("any");
                      setSearchQuery("");
                    }}
                    aria-pressed={rankMode === "shuffle"}
                    className={`inline-flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      rankMode === "shuffle"
                        ? "bg-highlight text-highlight-foreground shadow-sm"
                        : "bg-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Sparkles className="h-3 w-3" />
                    <span>Shake It Up</span>
                  </motion.button>
                  <button
                    onClick={() => { setRankMode("smart"); setShakeStuck(false); }}
                    aria-pressed={rankMode === "smart"}
                    className={`inline-flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      rankMode === "smart"
                        ? "bg-primary text-white shadow-sm"
                        : "bg-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Zap className="h-3 w-3" />
                    <span>Smart Ranked</span>
                  </button>
                </div>
              )}
            </div>

            <AnimatePresence>
              {!wizardData && !smartHintDismissed && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 rounded-lg px-3 py-2">
                    <Zap className="h-3 w-3 text-primary shrink-0" />
                    <span className="flex-1">
                      Take the 30-second quiz for picks tailored to you →{" "}
                      <Link to="/discover" className="font-semibold text-primary hover:underline">
                        Discover
                      </Link>
                    </span>
                    <button
                      onClick={() => {
                        setSmartHintDismissed(true);
                        try { sessionStorage.setItem("rp-smart-hint-dismissed", "1"); } catch {}
                      }}
                      className="p-1 rounded hover:bg-foreground/10 transition-colors shrink-0"
                      aria-label="Dismiss hint"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showFilters && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 pt-4 pb-2">
                    <FilterSelect label="Category" value={categoryFilter} onChange={setCategoryFilter}
                      options={[{ value: "all", label: "All Categories" }, ...categories.map((c) => ({ value: c, label: c }))]} />
                    <FilterSelect label="Setting" value={settingFilter} onChange={setSettingFilter}
                      options={[
                        { value: "all", label: "Indoor & Outdoor" },
                        { value: "indoor", label: "🏠 Indoor" },
                        { value: "outdoor", label: "🌳 Outdoor" },
                      ]} />
                    <FilterSelect label="Distance" value={distanceFilter} onChange={setDistanceFilter}
                      options={[
                        { value: "all", label: "Any Distance" },
                        { value: "15", label: hasUserOrigin ? "≤ 15 min from you" : "≤ 15 min" },
                        { value: "30", label: hasUserOrigin ? "≤ 30 min from you" : "≤ 30 min" },
                        { value: "60", label: hasUserOrigin ? "≤ 60 min from you" : "≤ 60 min" },
                        { value: "90", label: hasUserOrigin ? "≤ 90 min from you" : "≤ 90 min" },
                      ]} />
                    <FilterSelect label="Cost" value={costFilter} onChange={setCostFilter}
                      options={[
                        { value: "all", label: "♾️ No Limit" },
                        { value: "free", label: "🆓 Free" },
                        { value: "under25", label: "💵 Under $25" },
                        { value: "under75", label: "💸 Under $75" },
                      ]} />
                    <FilterSelect label="Time Available" value={durationFilter} onChange={(v) => setDurationFilter(v as DurationPreference)}
                      options={[
                        { value: "any", label: "♾️ Show all" },
                        { value: "short", label: "⚡ Under 2 hrs" },
                        { value: "half", label: "🕐 Half day" },
                        { value: "full", label: "🌞 Whole day" },
                      ]} />
                  </div>
                  {activeFilterCount > 0 && (
                    <button onClick={() => { setCategoryFilter("all"); setSettingFilter("all"); setDistanceFilter("all"); setCostFilter("all"); setDurationFilter("any"); }}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 mt-1">
                      <X className="h-3 w-3" /> Clear all filters
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Sticky Build Itinerary button */}
          <AnimatePresence>
            {savedIds.size > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-2xl bg-card/95 backdrop-blur-md border border-primary/30 shadow-2xl shadow-primary/20 px-5 py-3">
                <span className="text-sm font-semibold">
                  🎒 {savedIds.size} event{savedIds.size > 1 ? "s" : ""}
                </span>
                <button onClick={handleBuildItinerary}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 transition-all shadow-md">
                  <Compass className="h-4 w-4" />
                  Build Plan →
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* How to use — tabbed steps */}
          <HowToUseTabs />

          {/* Event cards */}
          {loading ? (
            <div className="space-y-5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-border p-6 animate-pulse" style={{ background: "var(--color-card)" }}>
                  <div className="h-4 w-24 bg-muted rounded mb-3" />
                  <div className="h-6 w-3/4 bg-muted rounded mb-2" />
                  <div className="h-4 w-full bg-muted rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-5">
              {filtered.map((event, i) => {
                const reciprocalBenefit = getReciprocalBenefit(event);
                const scheduleBadge = getScheduleBadge(event);
                const cardBorderColors = [
                  "var(--color-primary)",
                  "var(--color-accent)",
                  "var(--color-highlight)",
                ];
                const accentColor = cardBorderColors[i % 3];
                return (
                  <EventCard
                    key={event.id}
                    event={event}
                    index={i}
                    accentColor={accentColor}
                    scheduleLabel={scheduleBadge.label}
                    reciprocalBenefit={reciprocalBenefit}
                    costLabel={formatCost(event.cost_details, event.cost_level)}
                    mode="save"
                    saved={savedIds.has(event.id)}
                    onToggleSave={() => toggleSave(event.id)}
                    onTitleClick={() => trackLinkClick(event.id)}
                    driveMinutes={driveFor(event)}
                    driveMinutesLabel={driveTooltip}
                    userActions={user ? (
                      <EventUserActions
                        eventId={event.id}
                        visited={visitsMap.has(event.id)}
                        rating={ratingsMap.get(event.id) ?? null}
                        onChange={({ visited, rating }) => {
                          setVisitsMap((prev) => {
                            const next = new Map(prev);
                            if (visited) {
                              if (!next.has(event.id)) next.set(event.id, todayISO_EST());
                            } else {
                              next.delete(event.id);
                            }
                            return next;
                          });
                          setRatingsMap((prev) => {
                            const next = new Map(prev);
                            if (rating == null) next.delete(event.id);
                            else next.set(event.id, rating);
                            return next;
                          });
                        }}
                      />
                    ) : null}
                  />
                );
              })}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-xl font-semibold">No events match those filters</p>
              <p className="text-muted-foreground mt-2">Try loosening your criteria or <Link to="/discover" className="text-primary font-semibold hover:underline">take the quiz again</Link>.</p>
              {hasUserOrigin && !showBeyondCap && wizardData?.origin?.maxDriveMinutes && (
                <button
                  onClick={() => setShowBeyondCap(true)}
                  className="mt-3 text-sm font-semibold text-primary hover:underline"
                >
                  Show picks beyond your {wizardData.origin.maxDriveMinutes} min drive limit →
                </button>
              )}
            </div>
          )}

          {/* Bottom Shake It Up CTA */}
          {!loading && filtered.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-10 text-center"
            >
              <p className="text-muted-foreground text-sm mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Want more? Hit the button for a fresh batch of ideas ✨
              </p>
              <motion.button
                whileTap={{ rotate: 180, scale: 0.9 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
                onClick={() => {
                  setRankMode("shuffle");
                  setRandomSeed(Math.random());
                  setCategoryFilter("all");
                  setSettingFilter("all");
                  setDistanceFilter("all");
                  setCostFilter("all");
                  setDurationFilter("any");
                  setSearchQuery("");
                  document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
                  document.body.scrollTo({ top: 0, behavior: "smooth" });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold bg-highlight text-highlight-foreground hover:opacity-90 transition-colors cursor-pointer shadow-lg"
              >
                <Sparkles className="h-4 w-4" />
                Shake It Up Again
                <Sparkles className="h-4 w-4" />
              </motion.button>
            </motion.div>
          )}

        </div>
      </div>
    </TooltipProvider>
  );
}

function FilterSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{label}</label>
      <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-xl border border-input bg-card px-3 py-2.5 pr-8 text-sm font-medium transition-colors hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20">
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>
    </div>
  );
}

/* ─── How to Use — single compact card ─── */
function HowToUseTabs() {
  const { user } = useAuth();
  const grayBgActive = "color-mix(in srgb, var(--color-foreground) 8%, transparent)";
  return (
    <div className="mb-6">
      <div
        className="rounded-xl px-4 py-3"
        style={{ background: grayBgActive, color: "var(--color-foreground)" }}
      >
        {/* Top row: title/copy + bookmark button — mirrors EventCard layout */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h2
              className="text-base sm:text-lg font-bold leading-snug text-foreground"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              Browse <span className="text-foreground/40">›</span> Bookmark{" "}
              <span className="text-foreground/40">›</span> Build
            </h2>

            <p
              className="text-sm leading-snug mt-1"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Smart Ranked surfaces the best fit for you. Shake It Up to remix your picks.
            </p>
            <p
              className="text-sm leading-snug mt-1"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Bookmark the spots you like, then build your plan.
            </p>
            {user && (
              <p
                className="text-sm leading-snug mt-1"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Tap the stars on places you've been — your ratings sharpen future recommendations.
              </p>
            )}
          </div>

          {/* Sample bookmark button — matches EventCard, highlighted green */}
          <div
            aria-hidden="true"
            className="shrink-0 w-9 h-9 rounded-lg border-2 flex items-center justify-center pointer-events-none select-none"
            style={{
              background: "var(--color-highlight)",
              borderColor: "var(--color-highlight)",
              color: "var(--color-highlight-foreground)",
              boxShadow: "0 0 0 4px color-mix(in srgb, var(--color-highlight) 30%, transparent)",
            }}
          >
            <Bookmark className="h-4 w-4" />
          </div>
        </div>

        {/* Bubble legend */}
        <div className="mt-3 pt-3 border-t border-foreground/10">
          <p
            className="text-xs text-foreground/70 mb-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Each activity has quick-read info bubbles:
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-accent text-accent-foreground inline-flex items-center gap-1">
              Category
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground inline-flex items-center gap-1">
              <Car className="h-3 w-3" /> Drive
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground inline-flex items-center gap-1">
              <Sun className="h-3 w-3" /> Weather Fit
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-highlight text-highlight-foreground inline-flex items-center gap-1">
              Cost
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground inline-flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" /> Schedule
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> Duration
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

